import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Lock } from 'lucide-react';
import { toast } from 'sonner';

import { setSectionVisibility } from '../../../database/tournaments';
import { PUBLIC_SECTIONS } from '../../../lib/stages';
import { handleFirebaseError } from '../../../utils/errorHandler';
import { Switch } from '../../ui/switch';
import { cn } from '../../../lib/utils';

// Sticky strip above every staged tournament's sections: one switch per public
// section, live from the tournament doc. Rules have no switch — always on.
const PublicVisibilityBar = ({ tournament }) => {
  const [pending, setPending] = useState(() => new Set());

  const markPending = (key, isPending) =>
    setPending((prev) => {
      const next = new Set(prev);
      if (isPending) next.add(key);
      else next.delete(key);
      return next;
    });

  const toggle = async (key, label, next) => {
    markPending(key, true);
    try {
      await setSectionVisibility(tournament.id, key, next);
      toast.success(next ? `${label} is now public.` : `${label} is now hidden.`);
    } catch (error) {
      toast.error('Could not change visibility.', { description: handleFirebaseError(error) });
    } finally {
      markPending(key, false);
    }
  };

  return (
    <div className="z-20 -mx-4 border-b border-border/60 bg-background px-4 py-2 sm:-mx-6 sm:px-6 lg:sticky lg:top-16 lg:-mx-10 lg:px-10">
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <span
          className="w-full shrink-0 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground sm:w-auto"
          title="Switch on what visitors can see on the public tournament page."
        >
          Visible to the public
        </span>

        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-sm border border-border/60 px-2 py-1 text-xs text-muted-foreground">
          <Lock className="h-3 w-3" />
          Rules · always on
        </span>

        {PUBLIC_SECTIONS.map(({ key, label, hint }) => {
          const on = tournament?.visibility?.[key] === true;
          return (
            <label
              key={key}
              title={hint}
              className={cn(
                'inline-flex shrink-0 items-center gap-1.5 rounded-sm border px-2 py-1 text-xs',
                on ? 'border-heat/50 text-foreground' : 'border-border/60 text-muted-foreground'
              )}
            >
              <Switch
                checked={on}
                onCheckedChange={(next) => toggle(key, label, next)}
                disabled={pending.has(key)}
                aria-label={`Show ${label} on the public page`}
              />
              {label}
            </label>
          );
        })}

        <Link
          to={`/tournaments/${tournament.slug || tournament.id}`}
          className="flex shrink-0 items-center gap-1.5 font-mono text-xs text-muted-foreground hover:text-foreground sm:ml-auto"
        >
          <ExternalLink className="h-3 w-3" />
          View public page
        </Link>
      </div>
    </div>
  );
};

export default PublicVisibilityBar;
